import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

const NON_LOCKABLE_RECORD_TYPES = new Set(['custom-shop']);

export class RecordLockState {
  @tracked isLocked = false;
  @tracked isLocking = false;

  constructor(record, services) {
    this.record = record;
    this.confirm = services.confirm;
    this.currentUser = services.currentUser;
    this.fetch = services.fetch;
    this.flashMessages = services.flashMessages;
    this.intl = services.intl;
    this.store = services.store;
  }

  get isLockable() {
    const type = getRecordType(this.record);

    if (
      NON_LOCKABLE_RECORD_TYPES.has(type) ||
      !this.store.schema.hasResource({ type })
    ) {
      return false;
    }

    const fields = this.store.schema.fields({ type });
    const isLockable = fields.has('cmsLockerId');

    return isLockable && !this.record.isNew && !this.record.isDeleted;
  }

  async lockStatus(options = {}) {
    const { currentUserWarning = false, anotherUserWarning = true } = options;

    if (!this.isLockable) {
      return;
    }

    let response = await this._lockRequest('/lockStatus');
    let currentUserId = this.currentUser.id;
    let isLocked = response.status;
    let isLockedByCurrentUser = response.userId === currentUserId;

    if (response.userId && isLockedByCurrentUser) {
      if (currentUserWarning) {
        this.isLocked = true;
        this._showFlashIsLockedByCurrentUser();
      }
    } else if (isLocked) {
      this.isLocked = true;

      if (anotherUserWarning) {
        this._showFlashIsLockedByAnotherUser(response.userName);
      }
    } else {
      this.isLocked = false;
    }
  }

  async lock() {
    await this.lockStatus({ currentUserWarning: true });

    if (this.isLockable && !this.isLocked) {
      await this._lockRequest('/lock');
    }
  }

  async unlock() {
    await this.lockStatus({ anotherUserWarning: false });

    if (this.isLockable && !this.isLocked) {
      await this._lockRequest('/unlock');
    }
  }

  async forceUnlock() {
    if (this.isLockable) {
      await this._lockRequest('/unlock');
      await this.lock();
    }
  }

  async _lockRequest(url) {
    this.isLocking = true;

    try {
      return await this.fetch.post(url, {
        data: {
          entity: getRecordType(this.record),
          id: this.record.id,
        },
      });
    } finally {
      this.isLocking = false;
    }
  }

  _showFlashIsLockedByCurrentUser() {
    const scope = 'confirm.lockedByCurrentUser';

    this.confirm.ask(`${scope}.title`, `${scope}.message`).then((ok) => {
      if (!ok) {
        return;
      }

      this.forceUnlock();
    });
  }

  _showFlashIsLockedByAnotherUser(userName) {
    const scope = 'flash.lockedByAnotherUser';
    const message = this.intl.t(`${scope}.warning`, {
      user: userName,
      htmlSafe: true,
    });

    this.flashMessages.warning(message, { timeout: 8000, rawString: true });
  }
}

export default class RecordLocksService extends Service {
  @service confirm;
  @service currentUser;
  @service fetch;
  @service flashMessages;
  @service intl;
  @service store;

  locks = new WeakMap();

  isLockable(record) {
    return this._lockFor(record).isLockable;
  }

  isLocked(record) {
    return this._lockFor(record).isLocked;
  }

  isLocking(record) {
    return this._lockFor(record).isLocking;
  }

  lockStatus(record, options) {
    return this._lockFor(record).lockStatus(options);
  }

  lock(record) {
    return this._lockFor(record).lock();
  }

  unlock(record) {
    return this._lockFor(record).unlock();
  }

  forceUnlock(record) {
    return this._lockFor(record).forceUnlock();
  }

  _lockFor(record) {
    if (!this.locks.has(record)) {
      this.locks.set(record, new RecordLockState(record, this));
    }

    return this.locks.get(record);
  }
}
